<script setup lang="ts">
import { computed, ref } from "vue";
import { I18nT, useI18n } from "vue-i18n";

import type { Locale } from "@/shared/config";

import { useCompanyStore } from "@/entities/company/model";
import { UiButton, UiCheckbox, UiField, UiInput, startViewTransition } from "@/shared/ui";

const PERSONAL_DATA_LAW_URL = "https://zakon.rada.gov.ua/laws/show/2297-17";
/** Версии текста закона на других языках, для остальных — оригинал */
const PERSONAL_DATA_LAW_URLS: Partial<Record<Locale, string>> = {
    en: "https://zakon.rada.gov.ua/laws/show/2297-17?lang=en#Text",
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Контакты заполнены и сохранены в стор, сама заявка отправляется на следующем шаге */
const emit = defineEmits<{ submit: [] }>();

const { t, locale } = useI18n({ useScope: "global" });

const personalDataLawUrl = computed(
    () => PERSONAL_DATA_LAW_URLS[locale.value as Locale] ?? PERSONAL_DATA_LAW_URL,
);

// Fields are bound to the store, so the entered contacts survive leaving the screen
const companyStore = useCompanyStore();
const { contacts } = companyStore;
const isAgreed = ref(false);

/** Ошибки email и телефона показываем только после того, как пользователь ушёл с поля */
const isEmailTouched = ref(false);
const isEmailValid = computed(() => EMAIL_PATTERN.test(contacts.email.trim()));
const isEmailError = computed(() => isEmailTouched.value && !isEmailValid.value);

const isPhoneTouched = ref(false);
const isPhoneError = computed(() => isPhoneTouched.value && !companyStore.isPhoneValid);

const canSubmit = computed(
    () =>
        Boolean(
            contacts.companyName.trim() && contacts.firstName.trim() && contacts.lastName.trim(),
        ) &&
        isEmailValid.value &&
        companyStore.isPhoneValid &&
        isAgreed.value,
);

function touchEmail(): void {
    if (isEmailTouched.value || !contacts.email) return;
    void startViewTransition(() => (isEmailTouched.value = true));
}

function touchPhone(): void {
    if (isPhoneTouched.value || !contacts.phone) return;
    void startViewTransition(() => (isPhoneTouched.value = true));
}

function submit(): void {
    if (!canSubmit.value) return;

    companyStore.trimContacts();
    emit("submit");
}
</script>

<!-- Заявка компании на предложение по выкупу всей техники -->
<template>
    <form class="form" novalidate @submit.prevent="submit">
        <h3 class="title">{{ t("requestBuyoutOffer.title") }}</h3>
        <p class="subtitle">{{ t("requestBuyoutOffer.subtitle") }}</p>

        <UiField>
            <template #hintTop>{{ t("requestBuyoutOffer.companyName") }}</template>
            <UiInput
                v-model="contacts.companyName"
                :aria-label="t('requestBuyoutOffer.companyName')"
                autocomplete="organization"
            />
        </UiField>

        <UiField>
            <template #hintTop>{{ t("requestBuyoutOffer.firstName") }}</template>
            <UiInput
                v-model="contacts.firstName"
                :aria-label="t('requestBuyoutOffer.firstName')"
                autocomplete="given-name"
            />
        </UiField>

        <UiField>
            <template #hintTop>{{ t("requestBuyoutOffer.lastName") }}</template>
            <UiInput
                v-model="contacts.lastName"
                :aria-label="t('requestBuyoutOffer.lastName')"
                autocomplete="family-name"
            />
        </UiField>

        <UiField :is-error="isEmailError">
            <template #hintTop>{{ t("requestBuyoutOffer.email") }}</template>
            <UiInput
                v-model="contacts.email"
                type="email"
                :aria-label="t('requestBuyoutOffer.email')"
                autocomplete="email"
                :is-error="isEmailError"
                @blur="touchEmail"
            />
            <template v-if="isEmailError" #hintBottom>
                {{ t("requestBuyoutOffer.emailInvalid") }}
            </template>
        </UiField>

        <UiField :is-error="isPhoneError">
            <template #hintTop>{{ t("requestBuyoutOffer.phone") }}</template>
            <UiInput
                v-model="contacts.phone"
                type="tel"
                :aria-label="t('requestBuyoutOffer.phone')"
                :placeholder="t('requestBuyoutOffer.optional')"
                autocomplete="tel"
                :is-error="isPhoneError"
                @blur="touchPhone"
            />
            <template v-if="isPhoneError" #hintBottom>
                {{ t("requestBuyoutOffer.phoneInvalid") }}
            </template>
        </UiField>

        <UiCheckbox v-model="isAgreed" class="consent">
            <I18nT keypath="requestBuyoutOffer.consent" scope="global">
                <template #link>
                    <a
                        :href="personalDataLawUrl"
                        class="consent__link"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {{ t("requestBuyoutOffer.consentLink") }}
                    </a>
                </template>
            </I18nT>
        </UiCheckbox>

        <div class="actions">
            <slot name="back" />
            <UiButton type="submit" class="submit" :disabled="!canSubmit">
                {{ t("common.continue") }}
            </UiButton>
        </div>
    </form>
</template>

<style scoped>
.form {
    display: flex;
    flex-direction: column;
}

.title {
    margin: 8px 0;
    font-size: calc(var(--custom-font-size) * 1.143);
    font-weight: 700;
    line-height: 1.25;
}

.subtitle {
    margin: 0 0 20px;
    font-size: var(--custom-font-size);
    line-height: 1.429;
}

.consent {
    margin-top: 4px;
    margin-bottom: 16px;
}

.consent__link {
    color: var(--color-link);
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
}

.actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.submit {
    margin-left: auto;
}
</style>
